import { matchPath } from 'react-router-dom'
import {
  type LucideIcon,
  Bot,
  Settings,
  HelpCircle,
  MessageSquareText,
  FlaskConical,
  BarChart3,
  BookOpen,
  PhoneForwarded,
  Zap,
  Shapes,
  Play,
  Key,
  Building2,
  Handshake,
  Gauge,
  TestTube2,
  Webhook,
  Workflow,
} from 'lucide-react'

type RouteRegistryItem = {
  id: string
  path: string
  label: string
  headerPosition?: 'relative' | 'absolute'
}

/**
 * Query parameters for knowledge page tabs
 */
export const KNOWLEDGE_TAB_PARAMS = {
  KNOWLEDGE_BASES: 'knowledge-bases',
  LINKS: 'links',
  PRONUNCIATION_GUIDES: 'pronunciation-guides',
  LISTINGS: 'listings',
} as const

// Route registry - single source of truth for route segments
export const ROUTE_REGISTRY: Record<string, RouteRegistryItem> = {
  REPORTING: {
    id: 'REPORTING',
    path: '/dashboard/reporting',
    label: 'Dashboard',
  },
  ACTIVITY: {
    id: 'ACTIVITY',
    path: '/dashboard/activity',
    label: 'Activity',
    headerPosition: 'relative',
  },
  PLAYGROUND: {
    id: 'PLAYGROUND',
    path: '/playground',
    label: 'Playground',
  },
  AGENTS: {
    id: 'AGENTS',
    path: '/agents',
    label: 'Agents',
  },
  AGENTS_VOICE: {
    id: 'AGENTS_VOICE',
    path: '/agents/voice',
    label: 'Voice Settings',
  },
  AGENTS_OUTBOUND: {
    id: 'AGENTS_OUTBOUND',
    path: '/agents/outbound',
    label: 'Outbound Agent',
  },
  AGENTS_NEW: {
    id: 'AGENTS_NEW',
    path: '/agents/new',
    label: 'New Agent',
  },
  AGENTS_DETAIL: {
    id: 'AGENTS_DETAIL',
    path: '/agents/:id',
    label: 'Agent Details',
  },
  KNOWLEDGE: {
    id: 'KNOWLEDGE',
    path: '/knowledge',
    label: 'Knowledge',
  },
  ACTIONS: {
    id: 'ACTIONS',
    path: '/actions',
    label: 'Handoff Rules',
  },
  SCENARIOS: {
    id: 'SCENARIOS',
    path: '/scenarios',
    label: 'Scenarios',
  },
  NODE_AGENTS: {
    id: 'NODE_AGENTS',
    path: '/node-agents',
    label: 'Node Agents',
  },
  NODE_AGENTS_DETAIL: {
    id: 'NODE_AGENTS_DETAIL',
    path: '/node-agents/:id',
    label: 'Node Agent Details',
  },
  TRIGGERS: {
    id: 'TRIGGERS',
    path: '/triggers',
    label: 'Triggers',
  },
  TRIGGERS_NEW: {
    id: 'TRIGGERS_NEW',
    path: '/triggers/new',
    label: 'New Trigger',
  },
  TRIGGERS_DETAIL: {
    id: 'TRIGGERS_DETAIL',
    path: '/triggers/:id',
    label: 'Trigger Details',
  },
  SCENARIOS_NEW: {
    id: 'SCENARIOS_NEW',
    path: '/scenarios/new',
    label: 'New Scenario',
  },
  SCENARIOS_DETAIL: {
    id: 'SCENARIOS_DETAIL',
    path: '/scenarios/:id',
    label: 'Scenario Details',
  },
  NODE_ACTIONS: {
    id: 'NODE_ACTIONS',
    path: '/node-actions',
    label: 'Actions',
  },
  NODE_ACTIONS_NEW: {
    id: 'NODE_ACTIONS_NEW',
    path: '/node-actions/new',
    label: 'New Action',
  },
  NODE_ACTIONS_DETAIL: {
    id: 'NODE_ACTIONS_DETAIL',
    path: '/node-actions/:id',
    label: 'Action Details',
  },
  NODE_ACTION_GROUPS_NEW: {
    id: 'NODE_ACTION_GROUPS_NEW',
    path: '/node-actions/groups/new',
    label: 'New Action Group',
  },
  NODE_ACTION_GROUPS_DETAIL: {
    id: 'NODE_ACTION_GROUPS_DETAIL',
    path: '/node-actions/groups/:id',
    label: 'Action Group Details',
  },
  SETTINGS: {
    id: 'SETTINGS',
    path: '/settings',
    label: 'Settings',
  },
  SETTINGS_COMPANY: {
    id: 'SETTINGS_COMPANY',
    path: '/settings/company',
    label: 'Company',
  },
  SETTINGS_CHANNELS: {
    id: 'SETTINGS_CHANNELS',
    path: '/settings/channels',
    label: 'Channels',
  },
  SETTINGS_API_KEYS: {
    id: 'SETTINGS_API_KEYS',
    path: '/settings/api-keys',
    label: 'API Keys',
  },
  SETTINGS_INTEGRATIONS: {
    id: 'SETTINGS_INTEGRATIONS',
    path: '/settings/integrations',
    label: 'Integrations',
  },
  SETTINGS_INTEGRATIONS_PROPERTY_MELD: {
    id: 'SETTINGS_INTEGRATIONS_PROPERTY_MELD',
    path: '/settings/integrations/property-meld',
    label: 'Property Meld',
  },
  SETTINGS_INTEGRATIONS_SHOWMOJO: {
    id: 'SETTINGS_INTEGRATIONS_SHOWMOJO',
    path: '/settings/integrations/showmojo',
    label: 'ShowMojo',
  },
  SETTINGS_INTEGRATIONS_RENT_MANAGER: {
    id: 'SETTINGS_INTEGRATIONS_RENT_MANAGER',
    path: '/settings/integrations/rent-manager',
    label: 'Rent Manager',
  },
  SETTINGS_INTEGRATIONS_BUILDING_LINK: {
    id: 'SETTINGS_INTEGRATIONS_BUILDING_LINK',
    path: '/settings/integrations/building-link',
    label: 'BuildingLink',
  },
  SETTINGS_INTEGRATIONS_BUILDIUM: {
    id: 'SETTINGS_INTEGRATIONS_BUILDIUM',
    path: '/settings/integrations/buildium',
    label: 'Buildium',
  },
  SETTINGS_INTEGRATIONS_HUBSPOT: {
    id: 'SETTINGS_INTEGRATIONS_HUBSPOT',
    path: '/settings/integrations/hubspot',
    label: 'HubSpot',
  },
  SETTINGS_INTEGRATIONS_APPFOLIO: {
    id: 'SETTINGS_INTEGRATIONS_APPFOLIO',
    path: '/settings/integrations/appfolio',
    label: 'AppFolio',
  },
  SETTINGS_INTEGRATIONS_ENTRATA: {
    id: 'SETTINGS_INTEGRATIONS_ENTRATA',
    path: '/settings/integrations/entrata',
    label: 'Entrata',
  },
  SETTINGS_INTEGRATIONS_YARDI_VOYAGER: {
    id: 'SETTINGS_INTEGRATIONS_YARDI_VOYAGER',
    path: '/settings/integrations/yardi-voyager',
    label: 'Yardi Voyager',
  },
  SETTINGS_INTEGRATIONS_YARDI_BREEZE: {
    id: 'SETTINGS_INTEGRATIONS_YARDI_BREEZE',
    path: '/settings/integrations/yardi-breeze',
    label: 'Yardi Breeze',
  },
  SETTINGS_INTEGRATIONS_EPIC_PM: {
    id: 'SETTINGS_INTEGRATIONS_EPIC_PM',
    path: '/settings/integrations/epic-pm',
    label: 'Epic PM',
  },
  SETTINGS_INTEGRATIONS_RENTVINE: {
    id: 'SETTINGS_INTEGRATIONS_RENTVINE',
    path: '/settings/integrations/rentvine',
    label: 'Rentvine',
  },
  SETTINGS_INTEGRATIONS_DOORLOOP: {
    id: 'SETTINGS_INTEGRATIONS_DOORLOOP',
    path: '/settings/integrations/doorloop',
    label: 'DoorLoop',
  },
  SETTINGS_BILLING: {
    id: 'SETTINGS_BILLING',
    path: '/settings/billing',
    label: 'Plan & Billing',
  },
  SETTINGS_DIRECTORY: {
    id: 'SETTINGS_DIRECTORY',
    path: '/settings/directory',
    label: 'Directory',
  },
  SETTINGS_TEAM: {
    id: 'SETTINGS_TEAM',
    path: '/settings/team',
    label: 'Team',
  },
  ACTIONS_HANDOFF_NEW: {
    id: 'ACTIONS_HANDOFF_NEW',
    path: '/actions/handoff/new',
    label: 'New handoff rule',
  },
  ACTIONS_HANDOFF_DETAIL: {
    id: 'ACTIONS_HANDOFF_DETAIL',
    path: '/actions/handoff/:id',
    label: 'Handoff rule',
  },
  PARTNER_PARTNERS: {
    id: 'PARTNER_PARTNERS',
    path: '/partner/partners',
    label: 'Partners',
  },
  PARTNER_PARTNERS_ACTIVITY: {
    id: 'PARTNER_PARTNERS_ACTIVITY',
    path: '/partner/partners/:id/activity',
    label: 'Partner audit',
  },
  PARTNER_CREDENTIALS: {
    id: 'PARTNER_CREDENTIALS',
    path: '/partner/credentials',
    label: 'OAuth credentials',
  },
  PARTNER_COMPANIES: {
    id: 'PARTNER_COMPANIES',
    path: '/partner/companies',
    label: 'Provisioned companies',
  },
  PERFORMANCE: {
    id: 'PERFORMANCE',
    path: '/dashboard/performance',
    label: 'Performance',
  },
  TRIGGERS_EVENTS: {
    id: 'TRIGGERS_EVENTS',
    path: '/triggers/events',
    label: 'Events',
  },
  WEBHOOKS: {
    id: 'WEBHOOKS',
    path: '/webhooks',
    label: 'Webhooks',
  },
  WEBHOOKS_DELIVERIES: {
    id: 'WEBHOOKS_DELIVERIES',
    path: '/webhooks/deliveries',
    label: 'Deliveries',
  },
  EVALUATIONS: {
    id: 'EVALUATIONS',
    path: '/evaluations',
    label: 'Evaluations',
  },
  WEBHOOKS_ENDPOINTS: {
    id: 'WEBHOOKS_ENDPOINTS',
    path: '/webhooks/endpoints',
    label: 'Endpoints',
  },
  HELP: {
    id: 'HELP',
    path: 'https://hiresuper.com/help',
    label: 'Help Center',
  },
  DASHBOARD: {
    id: 'DASHBOARD',
    path: '/dashboard',
    label: 'Dashboard',
  },
  DASHBOARD_USERS: {
    id: 'DASHBOARD_USERS',
    path: '/dashboard/users',
    label: 'Users Management',
  },
  DASHBOARD_USER_PROFILE: {
    id: 'DASHBOARD_USER_PROFILE',
    path: '/dashboard/user-profile',
    label: 'User Profile',
  },
  DASHBOARD_USER_SESSIONS: {
    id: 'DASHBOARD_USER_SESSIONS',
    path: '/dashboard/user-sessions',
    label: 'User Sessions',
  },
  DASHBOARD_USER_SECURITY: {
    id: 'DASHBOARD_USER_SECURITY',
    path: '/dashboard/user-security',
    label: 'User Security',
  },
  DASHBOARD_ORGANIZATION_SWITCHER: {
    id: 'DASHBOARD_ORGANIZATION_SWITCHER',
    path: '/dashboard/organization-switcher',
    label: 'Organization Switcher',
  },
} as const

/**
 * Build path for a route with dynamic segments (e.g. :id).
 * Use instead of hardcoding paths to avoid dead links when routes change.
 */
export function pathFor(
  routeId: RouteId,
  params: Record<string, string> = {},
): string {
  let path = ROUTE_REGISTRY[routeId].path
  for (const [key, value] of Object.entries(params)) {
    path = path.replace(`:${key}`, value)
  }
  return path
}

/**
 * Build knowledge page path with optional tab and create query params.
 * Use this instead of hardcoding paths to avoid dead links when routes change.
 */
export function getKnowledgePath(options?: {
  tab?: (typeof KNOWLEDGE_TAB_PARAMS)[keyof typeof KNOWLEDGE_TAB_PARAMS]
  create?: boolean
}): string {
  const path = ROUTE_REGISTRY.KNOWLEDGE.path
  if (!options?.tab && !options?.create) return path
  const params = new URLSearchParams()
  if (options?.tab) params.set('tab', options.tab)
  if (options?.create) params.set('create', 'true')
  return `${path}?${params.toString()}`
}

export type RouteId = keyof typeof ROUTE_REGISTRY
export type RouteSegment = (typeof ROUTE_REGISTRY)[RouteId]

export type BreadcrumbConfig = Array<{
  label: string
  href?: string
}>

export type RouteItem = {
  id: RouteId
  title: string
  path: string
  breadcrumbs: BreadcrumbConfig
}

export type RouteConfig = {
  id: RouteId
  title: string
  path: string
  icon: LucideIcon
  breadcrumbs: BreadcrumbConfig
  section?: 'main' | 'footer'
  group?: string // Group label for organizing menu items
  adminOnly?: boolean
  featureFlag?: string
  featureFlagDisabled?: string // Show only when this flag is OFF
  addAction?: string // Path to navigate when + is clicked (shows + on hover)
  items?: Omit<RouteConfig, 'icon' | 'items'>[]
}

// Helper to build breadcrumbs based on route IDs
export function buildBreadcrumbs(routeId: RouteId): BreadcrumbConfig {
  // Special handling for dashboard routes
  if (routeId.startsWith('DASHBOARD_')) {
    return [
      {
        label: ROUTE_REGISTRY.DASHBOARD.label,
        href: ROUTE_REGISTRY.DASHBOARD.path,
      },
      {
        label: ROUTE_REGISTRY[routeId].label,
        href: ROUTE_REGISTRY[routeId].path,
      },
    ]
  }

  // Build breadcrumbs by progressively checking for existing routes
  const segments = routeId.split('_')
  const breadcrumbs: BreadcrumbConfig = []

  for (let index = 0; index < segments.length; index++) {
    const currentId = segments.slice(0, index + 1).join('_') as RouteId
    const route = ROUTE_REGISTRY[currentId]

    // Only add to breadcrumbs if the route exists in the registry
    if (route) {
      breadcrumbs.push({
        label: route.label,
        href: route.path,
      })
    }
  }

  // If we didn't find any breadcrumbs, throw an error
  if (breadcrumbs.length === 0) {
    throw new Error(`No valid routes found for ID: ${routeId}`)
  }

  return breadcrumbs
}

// Main navigation routes with icons and subitems
export const ROUTES: Record<string, RouteConfig> = {
  // === Monitor Section ===
  REPORTING: {
    id: ROUTE_REGISTRY.REPORTING.id,
    title: ROUTE_REGISTRY.REPORTING.label,
    path: ROUTE_REGISTRY.REPORTING.path,
    icon: BarChart3,
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.REPORTING.id),
    group: 'Monitor',
  },
  ACTIVITY: {
    id: ROUTE_REGISTRY.ACTIVITY.id,
    title: ROUTE_REGISTRY.ACTIVITY.label,
    path: ROUTE_REGISTRY.ACTIVITY.path,
    icon: MessageSquareText,
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.ACTIVITY.id),
    group: 'Monitor',
  },
  PERFORMANCE: {
    id: ROUTE_REGISTRY.PERFORMANCE.id,
    title: ROUTE_REGISTRY.PERFORMANCE.label,
    path: ROUTE_REGISTRY.PERFORMANCE.path,
    icon: Gauge,
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.PERFORMANCE.id),
    group: 'Monitor',
    adminOnly: true,
  },

  // === Configure Section ===
  TRIGGERS: {
    id: ROUTE_REGISTRY.TRIGGERS.id,
    title: ROUTE_REGISTRY.TRIGGERS.label,
    path: ROUTE_REGISTRY.TRIGGERS.path,
    icon: Play,
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.TRIGGERS.id),
    group: 'Configure',
  },
  SCENARIOS: {
    id: ROUTE_REGISTRY.SCENARIOS.id,
    title: ROUTE_REGISTRY.SCENARIOS.label,
    path: ROUTE_REGISTRY.SCENARIOS.path,
    icon: Shapes,
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.SCENARIOS.id),
    group: 'Configure',
    featureFlag: 'outbound',
  },
  NODE_AGENTS: {
    id: ROUTE_REGISTRY.NODE_AGENTS.id,
    title: ROUTE_REGISTRY.NODE_AGENTS.label,
    path: ROUTE_REGISTRY.NODE_AGENTS.path,
    icon: Workflow,
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.NODE_AGENTS.id),
    group: 'Configure',
    featureFlag: 'node-agents',
  },
  // Multi-agent list: always show (OUTBOUND flag only disables Outbound option in create modal and Prompt field)
  AGENTS: {
    id: ROUTE_REGISTRY.AGENTS.id,
    title: ROUTE_REGISTRY.AGENTS.label,
    path: ROUTE_REGISTRY.AGENTS.path,
    icon: Bot,
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.AGENTS.id),
    group: 'Configure',
    addAction: '/agents?create=true',
  },
  KNOWLEDGE: {
    id: ROUTE_REGISTRY.KNOWLEDGE.id,
    title: ROUTE_REGISTRY.KNOWLEDGE.label,
    path: ROUTE_REGISTRY.KNOWLEDGE.path,
    icon: BookOpen,
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.KNOWLEDGE.id),
    group: 'Configure',
  },
  ACTIONS: {
    id: ROUTE_REGISTRY.ACTIONS.id,
    title: ROUTE_REGISTRY.ACTIONS.label,
    path: ROUTE_REGISTRY.ACTIONS.path,
    icon: PhoneForwarded,
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.ACTIONS.id),
    group: 'Configure',
  },
  NODE_ACTIONS: {
    id: ROUTE_REGISTRY.NODE_ACTIONS.id,
    title: ROUTE_REGISTRY.NODE_ACTIONS.label,
    path: ROUTE_REGISTRY.NODE_ACTIONS.path,
    icon: Zap,
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.NODE_ACTIONS.id),
    group: 'Configure',
    featureFlag: 'node-agents',
  },

  WEBHOOKS: {
    id: ROUTE_REGISTRY.WEBHOOKS.id,
    title: ROUTE_REGISTRY.WEBHOOKS.label,
    path: ROUTE_REGISTRY.WEBHOOKS.path,
    icon: Webhook,
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.WEBHOOKS.id),
    group: 'Configure',
  },

  // === Admin Section ===
  EVALUATIONS: {
    id: ROUTE_REGISTRY.EVALUATIONS.id,
    title: ROUTE_REGISTRY.EVALUATIONS.label,
    path: ROUTE_REGISTRY.EVALUATIONS.path,
    icon: TestTube2,
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.EVALUATIONS.id),
    group: 'Admin',
    adminOnly: true,
  },
  PLAYGROUND: {
    id: ROUTE_REGISTRY.PLAYGROUND.id,
    title: ROUTE_REGISTRY.PLAYGROUND.label,
    path: ROUTE_REGISTRY.PLAYGROUND.path,
    icon: FlaskConical,
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.PLAYGROUND.id),
    group: 'Admin',
    adminOnly: true,
  },
  SETTINGS: {
    id: ROUTE_REGISTRY.SETTINGS.id,
    title: ROUTE_REGISTRY.SETTINGS.label,
    path: ROUTE_REGISTRY.SETTINGS.path,
    icon: Settings,
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.SETTINGS.id),
    group: 'Admin',
    items: [
      {
        id: ROUTE_REGISTRY.SETTINGS_COMPANY.id,
        title: ROUTE_REGISTRY.SETTINGS_COMPANY.label,
        path: ROUTE_REGISTRY.SETTINGS_COMPANY.path,
        breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.SETTINGS_COMPANY.id),
      },
      {
        id: ROUTE_REGISTRY.SETTINGS_CHANNELS.id,
        title: ROUTE_REGISTRY.SETTINGS_CHANNELS.label,
        path: ROUTE_REGISTRY.SETTINGS_CHANNELS.path,
        breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.SETTINGS_CHANNELS.id),
      },
      {
        id: ROUTE_REGISTRY.SETTINGS_API_KEYS.id,
        title: ROUTE_REGISTRY.SETTINGS_API_KEYS.label,
        path: ROUTE_REGISTRY.SETTINGS_API_KEYS.path,
        breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.SETTINGS_API_KEYS.id),
      },
      {
        id: ROUTE_REGISTRY.SETTINGS_INTEGRATIONS.id,
        title: ROUTE_REGISTRY.SETTINGS_INTEGRATIONS.label,
        path: ROUTE_REGISTRY.SETTINGS_INTEGRATIONS.path,
        breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.SETTINGS_INTEGRATIONS.id),
      },
      {
        id: ROUTE_REGISTRY.SETTINGS_BILLING.id,
        title: ROUTE_REGISTRY.SETTINGS_BILLING.label,
        path: ROUTE_REGISTRY.SETTINGS_BILLING.path,
        breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.SETTINGS_BILLING.id),
      },
      {
        id: ROUTE_REGISTRY.SETTINGS_DIRECTORY.id,
        title: ROUTE_REGISTRY.SETTINGS_DIRECTORY.label,
        path: ROUTE_REGISTRY.SETTINGS_DIRECTORY.path,
        breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.SETTINGS_DIRECTORY.id),
      },
      {
        id: ROUTE_REGISTRY.SETTINGS_TEAM.id,
        title: ROUTE_REGISTRY.SETTINGS_TEAM.label,
        path: ROUTE_REGISTRY.SETTINGS_TEAM.path,
        breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.SETTINGS_TEAM.id),
      },
    ],
  },
  // Partner Dashboard (partner users + super admins viewing a partner company)
  PARTNER_PARTNERS: {
    id: ROUTE_REGISTRY.PARTNER_PARTNERS.id,
    title: ROUTE_REGISTRY.PARTNER_PARTNERS.label,
    path: ROUTE_REGISTRY.PARTNER_PARTNERS.path,
    icon: Handshake,
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.PARTNER_PARTNERS.id),
    group: 'Partner',
    adminOnly: true,
  },
  PARTNER_CREDENTIALS: {
    id: ROUTE_REGISTRY.PARTNER_CREDENTIALS.id,
    title: ROUTE_REGISTRY.PARTNER_CREDENTIALS.label,
    path: ROUTE_REGISTRY.PARTNER_CREDENTIALS.path,
    icon: Key,
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.PARTNER_CREDENTIALS.id),
    group: 'Partner',
  },
  PARTNER_COMPANIES: {
    id: ROUTE_REGISTRY.PARTNER_COMPANIES.id,
    title: ROUTE_REGISTRY.PARTNER_COMPANIES.label,
    path: ROUTE_REGISTRY.PARTNER_COMPANIES.path,
    icon: Building2,
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.PARTNER_COMPANIES.id),
    group: 'Partner',
  },
  HELP: {
    id: ROUTE_REGISTRY.HELP.id,
    title: ROUTE_REGISTRY.HELP.label,
    path: ROUTE_REGISTRY.HELP.path,
    icon: HelpCircle,
    section: 'footer',
    breadcrumbs: buildBreadcrumbs(ROUTE_REGISTRY.HELP.id),
  },
}

// Helper to get all routes flattened (useful for route matching)
export const ALL_ROUTES = Object.values(ROUTES).reduce<RouteConfig[]>(
  (acc, route) => {
    if ('items' in route && route.items) {
      return [
        ...acc,
        route,
        ...(route.items.map(item => ({
          ...item,
          icon: route.icon,
        })) as RouteConfig[]),
      ]
    }
    return [...acc, route]
  },
  [],
)

// Resolves a pathname (e.g. `/scenarios/abc123`) against the route
// registry, preferring an exact static match before falling back to
// parameterised templates (e.g. `/scenarios/:id`). Uses react-router's
// `matchPath` so we don't reimplement path parsing.
export function getBreadcrumbs(path: string): BreadcrumbConfig {
  const all = Object.values(ROUTE_REGISTRY)
  const exact = all.find(r => r.path === path)
  if (exact) return buildBreadcrumbs(exact.id)
  const templated = all.find(
    r => r.path.includes(':') && matchPath(r.path, path) !== null,
  )
  return templated ? buildBreadcrumbs(templated.id) : []
}

// Helper to get a route by its ID
export function getRouteById(id: RouteId): RouteSegment {
  return ROUTE_REGISTRY[id]
}

// Helper to get a route's full configuration by its ID
export function getRouteConfigById(id: RouteId): RouteConfig | undefined {
  const mainRoute = Object.values(ROUTES).find(route => route.id === id)
  if (mainRoute) return mainRoute

  // Search in subitems if not found in main routes
  for (const route of Object.values(ROUTES)) {
    if (route.items) {
      const subRoute = route.items.find(item => item.id === id)
      if (subRoute) {
        return {
          ...subRoute,
          icon: route.icon,
        }
      }
    }
  }
}
